import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { whitelistAPI } from '../services/api';

const WhitelistManagement = () => {
  const [whitelistedUsers, setWhitelistedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    middleName: '',
    position: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchWhitelistedUsers();
  }, []);

  const fetchWhitelistedUsers = async () => {
    setLoading(true);
    try {
      const response = await whitelistAPI.getAllWhitelistedUsers();
      setWhitelistedUsers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching whitelisted users:', err);
      setError('Не вдалося завантажити список дозволених користувачів. Спробуйте пізніше.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        middleName: user.middleName || '',
        position: user.position || ''
      });
      setIsEditing(true);
    } else {
      setSelectedUser(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        middleName: '',
        position: ''
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSave = async () => {
    // Basic validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setSnackbar({
        open: true,
        message: 'Будь ласка, заповніть всі обов\'язкові поля',
        severity: 'error'
      });
      return;
    }

    try {
      let response;
      if (isEditing && selectedUser) {
        response = await whitelistAPI.updateWhitelistedUser(selectedUser.id, formData);
        setWhitelistedUsers(whitelistedUsers.map(user => 
          user.id === selectedUser.id ? response.data : user
        ));
        setSnackbar({
          open: true,
          message: `Користувача ${formData.email} оновлено в списку дозволених`,
          severity: 'success'
        });
      } else {
        response = await whitelistAPI.createWhitelistedUser(formData);
        setWhitelistedUsers([...whitelistedUsers, response.data]);
        setSnackbar({
          open: true,
          message: `Користувача ${formData.email} додано до списку дозволених`,
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving whitelisted user:', err);
      setSnackbar({
        open: true,
        message: `Помилка: ${err.response?.data?.message || err.message}`,
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await whitelistAPI.deleteWhitelistedUser(selectedUser.id);
      setWhitelistedUsers(whitelistedUsers.filter(user => user.id !== selectedUser.id));
      setSnackbar({
        open: true,
        message: `Користувача ${selectedUser.email} видалено зі списку дозволених`,
        severity: 'success'
      });
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting whitelisted user:', err);
      setSnackbar({
        open: true,
        message: `Помилка при видаленні: ${err.response?.data?.message || err.message}`,
        severity: 'error'
      });
      handleCloseDeleteDialog();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Список дозволених користувачів
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Додати користувача
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Прізвище</TableCell>
              <TableCell>Ім'я</TableCell>
              <TableCell>По батькові</TableCell>
              <TableCell>Посада</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {whitelistedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Список порожній. Додайте користувачів, яким дозволено входити в систему.
                </TableCell>
              </TableRow>
            ) : (
              whitelistedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.middleName || '-'}</TableCell>
                  <TableCell>{user.position || '-'}</TableCell>
                  <TableCell>
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpenDialog(user)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpenDeleteDialog(user)}
                      size="small"
                    >
                      <DeleteIcon color="primary" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for adding/editing whitelisted user */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Редагувати дозволеного користувача' : 'Додати дозволеного користувача'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isEditing 
              ? 'Редагуйте інформацію про дозволеного користувача.' 
              : 'Додайте нового користувача до списку дозволених для входу в систему.'}
          </DialogContentText>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email *"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="lastName"
                label="Прізвище *"
                value={formData.lastName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="firstName"
                label="Ім'я *"
                value={formData.firstName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="middleName"
                label="По батькові"
                value={formData.middleName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="position"
                label="Посада"
                value={formData.position}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {isEditing ? 'Зберегти зміни' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for confirming deletion */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете видалити користувача {selectedUser?.email} зі списку дозволених?
            Цей користувач більше не зможе увійти в систему.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Скасувати</Button>
          <Button onClick={handleDelete} color="error">
            Видалити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhitelistManagement;
